import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material'
import { useAuth } from '../../contexts/AuthContext'
import { User } from '../../types/auth'
import { NivelAcesso } from '../../types/auth'

// Schema de validação
const usuarioSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome deve ter no máximo 100 caracteres'),
  email: z.string().email('Email inválido').optional().nullable(),
  password: z
    .string()
    .min(6, 'Senha deve ter no mínimo 6 caracteres')
    .or(z.literal(''))
    .optional()
    .nullable(),
  is_active: z.boolean(),
  nivel_acesso: z.number().min(0).max(5).optional().nullable(),
  descricao_superadmin: z.string().optional().nullable(),
  login_thor: z
    .string()
    .max(16, 'login Thor deve ter no máximo 16 caracteres')
    .or(z.literal(''))
    .optional()
    .nullable(),
  iduser_thor: z
    .preprocess((val) => (val === '' || val === null || val === undefined ? null : Number(val)), z.number().int().positive().nullable())
    .optional()
    .nullable(),
})

type UsuarioFormData = z.infer<typeof usuarioSchema>

export interface UsuarioFormSubmitData {
  name: string
  email?: string | null
  password?: string | null
  is_active: boolean
  nivel_acesso?: number | null
  descricao_superadmin?: string | null
  login_thor?: string | null
  iduser_thor?: number | null
}

interface UsuarioFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: UsuarioFormSubmitData) => Promise<void>
  usuario?: User | null
  loading?: boolean
  mode: 'create' | 'edit'
}

const UsuarioForm: React.FC<UsuarioFormProps> = ({
  open,
  onClose,
  onSubmit,
  usuario,
  loading = false,
  mode,
}) => {
  const { user: currentUser } = useAuth()
  const isSuperAdmin = currentUser?.nivel_acesso === NivelAcesso.SUPER_ADMIN

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<UsuarioFormData>({
    resolver: zodResolver(usuarioSchema),
    defaultValues: {
      name: usuario?.name || '',
      email: usuario?.email || '',
      password: '',
      is_active: usuario?.is_active ?? true,
      nivel_acesso: usuario?.nivel_acesso || null,
      descricao_superadmin: usuario?.descricao_superadmin || '',
      login_thor: usuario?.login_thor || '',
      iduser_thor: usuario?.iduser_thor || null,
    },
  })

  const isActive = watch('is_active')
  const nivelAcesso = watch('nivel_acesso')

  // Resetar formulário quando usuário mudar ou dialog abrir/fechar
  useEffect(() => {
    if (open) {
      reset({
        name: usuario?.name || '',
        email: usuario?.email || '',
        password: '',
        is_active: usuario?.is_active ?? true,
        nivel_acesso: usuario?.nivel_acesso || null,
        descricao_superadmin: usuario?.descricao_superadmin || '',
        login_thor: usuario?.login_thor || '',
        iduser_thor: usuario?.iduser_thor || null,
      })
    }
  }, [open, usuario, reset])

  const handleFormSubmit = async (data: UsuarioFormData) => {
    try {
      if (mode === 'create') {
        if (!data.email || data.email.trim().length === 0) {
          setError('email', { type: 'manual', message: 'Email é obrigatório' })
          return
        }
        if (!data.password || data.password.trim().length < 6) {
          setError('password', { type: 'manual', message: 'Senha deve ter no mínimo 6 caracteres' })
          return
        }
      }

      const submitData: UsuarioFormSubmitData = {
        name: data.name.trim(),
        email: mode === 'create' ? data.email?.trim() ?? '' : usuario?.email ?? '',
        password: mode === 'create' ? data.password?.trim() ?? '' : undefined,
        is_active: data.is_active,
      }
      
      // Só incluir nivel_acesso se for Super Admin
      if (isSuperAdmin && data.nivel_acesso !== null && data.nivel_acesso !== undefined) {
        submitData.nivel_acesso = data.nivel_acesso
      }
      // Campo exclusivo do super admin
      if (isSuperAdmin) {
        submitData.descricao_superadmin = (data.descricao_superadmin ?? '').toString().trim() || null
        submitData.login_thor = (data.login_thor ?? '').toString().trim() || null
        submitData.iduser_thor = data.iduser_thor !== undefined && data.iduser_thor !== null ? Number(data.iduser_thor) : null
      }
      
      await onSubmit(submitData)
      reset()
      onClose()
    } catch (error) {
      // Erro já é tratado pelo componente pai via onError
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>{mode === 'create' ? 'Criar Usuário' : 'Editar Usuário'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              {...register('name')}
              label="Nome"
              fullWidth
              margin="normal"
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={loading}
              autoFocus
            />

            <TextField
              {...register('email')}
              label="Email"
              fullWidth
              margin="normal"
              type="email"
              disabled={mode === 'edit'}
              helperText={errors.email?.message || (mode === 'edit' ? 'Email não pode ser alterado' : '')}
              error={!!errors.email}
            />

            {mode === 'create' && (
              <TextField
                {...register('password')}
                label="Senha"
                fullWidth
                margin="normal"
                type="password"
                error={!!errors.password}
                helperText={errors.password?.message || 'Mínimo de 6 caracteres'}
                disabled={loading}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setValue('is_active', e.target.checked)}
                  disabled={loading}
                />
              }
              label="Usuário Ativo"
              sx={{ mt: 2 }}
            />

            {/* Campo login_thor - visível para todos, mas editável apenas para Super Admin */}
            <TextField
              {...register('login_thor')}
              label="Login ThorJava (até 16 caracteres, case sensitive)"
              fullWidth
              margin="normal"
              disabled={loading || !isSuperAdmin}
              inputProps={{ maxLength: 16 }}
              helperText={
                errors.login_thor?.message || 
                (isSuperAdmin 
                  ? 'Corresponde ao campo login da vw_uccaduser. Pode ser usado para login alternativo ao email.' 
                  : 'Apenas Super Administradores podem editar este campo')
              }
              error={!!errors.login_thor}
            />

            {/* Campo iduser_thor - visível para todos, mas editável apenas para Super Admin */}
            <TextField
              {...register('iduser_thor')}
              label="ID Usuário ThorJava (Inteiro)"
              fullWidth
              margin="normal"
              type="number"
              disabled={loading || !isSuperAdmin}
              helperText={
                errors.iduser_thor?.message || 
                (isSuperAdmin 
                  ? 'Corresponde à PK iduser da tabela uccaduser. Usado para sincronização precisa entre sistemas.' 
                  : 'Apenas Super Administradores podem editar este campo')
              }
              error={!!errors.iduser_thor}
            />

            {isSuperAdmin && (
              <>
              <FormControl fullWidth margin="normal">
                <InputLabel>Nível de Acesso</InputLabel>
                <Select
                  value={nivelAcesso ?? ''}
                  label="Nível de Acesso"
                  disabled={loading}
                  onChange={(e) => setValue('nivel_acesso', Number(e.target.value))}
                >
                  <MenuItem value={NivelAcesso.SUPER_ADMIN}>Super Administrador</MenuItem>
                  <MenuItem value={NivelAcesso.ADMINISTRADOR}>Administrador</MenuItem>
                  <MenuItem value={NivelAcesso.GERENTE}>Gerente</MenuItem>
                  <MenuItem value={NivelAcesso.SUPERVISOR}>Supervisor</MenuItem>
                  <MenuItem value={NivelAcesso.OPERADOR}>Operador</MenuItem>
                  <MenuItem value={NivelAcesso.USUARIO_PADRAO}>Usuário Padrão</MenuItem>
                </Select>
              </FormControl>
              <TextField
                {...register('descricao_superadmin')}
                label="Descrição do Super Administrador"
                fullWidth
                margin="normal"
                multiline
                rows={3}
                disabled={loading}
                helperText="Visível no cadastro de usuários; não aparece na manutenção"
              />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default UsuarioForm


